// GROUND-M node decode + stitch (groundtree.js): one node's bytes -> stitch.js
// baseOf, with the geomorph delta taken against its PARENT's decoded mesh (the
// owner's "from-heights computed in the worker": nothing extra downloaded);
// and the drawn set's stitch (stitch.js, unchanged) -> per node the arrays its
// geometry is made of. Run in the decode worker (gmworker.js: GmCore) or,
// without one, on the main thread — the same code. `cache` holds the decoded
// meshes (indexNode'd) a child's delta reads; the caller drops a key when it
// evicts the node.
import { decodeNode, meshOf } from '../../groundm/codec.js';
import { indexNode, nodeSide } from '../../groundm/lookup.js';
import { baseOf, stitchPlan, stitchStep, stitchDone } from '../../groundm/stitch.js';
import { curveDrop } from './curve.js';

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

// job = { key, k, tx, ty, bytes (ArrayBuffer), parent (key | null),
// parentBytes (its bytes: decoded here when the cache lacks it — a worker
// restart, a main-thread fallback, a parent the worker's LRU let go), portal }
// -> { base, ms } | { wait: parent key } (no parent mesh and no bytes)
const meshOfBytes = (bytes, k) => indexNode({ ...meshOf(decodeNode(new Uint8Array(bytes))), level: k });
export function decodeBase(cache, job) {
  const t0 = now();
  let par = job.parent ? cache.get(job.parent) : null;
  if (job.parent && !par) {
    if (!job.parentBytes) return { wait: job.parent };
    par = meshOfBytes(job.parentBytes, job.k + 1);
    cache.set(job.parent, par);
  }
  const mesh = meshOfBytes(job.bytes, job.k);
  cache.set(job.key, mesh);
  const base = baseOf(mesh, job.k, job.tx, job.ty, par, { portal: !!job.portal });
  return { base, ms: now() - t0 };
}

// the base's typed arrays' buffers (postMessage transfer list)
export function transferOf(base) {
  const out = new Set();
  const add = (v) => { if (v && ArrayBuffer.isView(v)) out.add(v.buffer); };
  for (const v of Object.values(base)) {
    if (Array.isArray(v)) v.forEach(add); else add(v);
  }
  return [...out];
}

// a base with its own arrays (the worker keeps one, posts the other)
export function cloneBase(base) {
  const out = {};
  for (const [k, v] of Object.entries(base)) {
    out[k] = ArrayBuffer.isView(v) ? v.slice() : Array.isArray(v) ? v.map((a) => (ArrayBuffer.isView(a) ? a.slice() : a)) : v;
  }
  return out;
}

// a stitched Work -> what its geometry is made of: the world position (x, aH,
// z; f32 — a shared vertex is one world mm on both sides, so the two copies
// are bit-identical), the i8 delta code (stitch.js DQ), the index (u16 below
// 65536 vertices). curve (curve.js curveOf, null: none): aH less the earth
// drop at the vertex's world xz — 0 inside the frame, so a frame vertex keeps
// its height bit for bit; the delta code stays the flat one (the morph's
// target then differs from the curved coarse edge by its sag, <= k L^2 / 4)
export function geoArrays(W, k, tx, ty, curve = null) {
  const o = W.out(), nv = o.pos.length / 3, s = nodeSide(k), ox = tx * s * 1000, oz = ty * s * 1000;
  const pos = new Float32Array(3 * nv);
  for (let v = 0; v < nv; v++) {
    const x = (ox + W.xm[v]) / 1000, z = (oz + W.zm[v]) / 1000;
    pos[3 * v] = x; pos[3 * v + 1] = W.h[v] - curveDrop(curve, x, z); pos[3 * v + 2] = z;
  }
  return { pos, dc: o.dc, idx: nv < 65536 ? Uint16Array.from(o.idx) : o.idx };
}

// a drawn set as a message: [[entry key, { n: { k, tx, ty }, quad, m, fk, fx, fy }]]
// in the Map's order (stitchPlan's order, so the same bytes)
export const drawMsg = (draw) => [...draw].map(([pk, e]) => [pk, { n: { k: e.n.k, tx: e.n.tx, ty: e.n.ty }, quad: e.quad, m: e.m, fk: e.fk, fx: e.fx, fy: e.fy }]);

const MESH_KEEP = 48;        // decoded meshes kept for a child's delta (LRU; a miss: wait -> parentBytes)
const BASE_KEEP = 64;        // bases kept beyond the last drawn set (LRU; a miss: the caller resends them)

// the worker's state: the decoded meshes (LRU), the bases (the last drawn
// set's, plus the BASE_KEEP last used — the main thread holds every
// resident one and resends what a stitch misses), the stitch cache
// (stitch.js opt.cache: the last set's Works)
export class GmCore {
  constructor(keep = MESH_KEEP, keepBases = BASE_KEEP) {
    this.meshes = new Map();
    this.bases = new Map();
    this.sc = new Map();
    this.keep = keep;
    this.keepBases = keepBases;
    this.drawn = new Set();
  }

  _base(key, b) {
    const B = this.bases;
    B.delete(key); B.set(key, b);
    for (const k of B.keys()) {
      if (B.size <= this.keepBases + this.drawn.size) break;
      if (!this.drawn.has(k) && k !== key) B.delete(k);
    }
  }

  // -> { base (a copy: the caller may transfer it), ms } | { wait }
  decode(job) {
    const M = this.meshes;
    if (job.parent && M.has(job.parent)) { const p = M.get(job.parent); M.delete(job.parent); M.set(job.parent, p); }
    const r = decodeBase(M, job);
    if (r.wait) return r;
    while (M.size > this.keep) M.delete(M.keys().next().value);
    this._base(job.key, r.base);
    return { base: cloneBase(r.base), ms: r.ms };
  }

  drop(key) { this.meshes.delete(key); this.bases.delete(key); this.sc.delete(key); }

  // req = { draw: drawMsg, top, have: { node key: the sig its shown geometry
  // was made of }, bases?: [[node key, base]] (resent), curve (geoArrays) } -> { keys, geo:
  // [[key, sig, geoArrays]] where the sig differs from `have`, ms } |
  // { missing: [node key] } (no base here: resend them)
  stitch(req) {
    const t0 = now(), draw = new Map(req.draw), have = req.have || {};
    const keys = new Set([...draw.values()].map((e) => `${e.n.k}_${e.n.tx}_${e.n.ty}`));
    this.drawn = keys;
    for (const [key, b] of req.bases || []) this._base(key, b);
    const missing = [...keys].filter((key) => !this.bases.has(key));
    if (missing.length) return { missing };
    for (const key of keys) this._base(key, this.bases.get(key));
    const P = stitchPlan(draw, this.bases, req.top, { cache: this.sc });
    while (stitchStep(P));
    const res = stitchDone(P), geo = [];
    for (const [key, { work, sig }] of res) {
      if (have[key] === sig) continue;
      const B = this.bases.get(key);
      geo.push([key, sig, geoArrays(work, B.k, B.tx, B.ty, req.curve || null)]);
    }
    return { keys: [...res.keys()], geo, ms: now() - t0 };
  }
}
